import { v4 as uuid } from 'uuid'
import { clipSpeed, sourceTimeAt } from '@/types/timeline-editing'
import { windowVolumePoints } from '@/types/timeline-editing'
/**
 * Timeline utilities for snapping and clip manipulation
 */

import { MAX_IMAGE_ASSET_DURATION_TICKS, secondsToTicks } from './constants'
import type { Asset, Clip } from '@/types'

/**
 * Snap threshold in ticks (~100ms)
 */
export const SNAP_THRESHOLD_TICKS = secondsToTicks(0.1)

type TimelineTrimAsset = Pick<Asset, 'assetType' | 'mimeType' | 'duration'>

function isTimelineImageAsset(asset: TimelineTrimAsset): boolean {
  if (asset.assetType != null) return asset.assetType === 'image'
  return asset.mimeType?.startsWith('image/') === true
}

/**
 * Return the source trim limit for timeline right-edge resizing.
 *
 * Still images are duration-flexible timeline sources. Some older projects have
 * image/frame assets whose stored asset.duration is 0 or one frame; using that
 * persisted value as the max trim-out makes the right trim handle collapse.
 */
export function getTimelineTrimOutLimit(asset: TimelineTrimAsset | undefined, fallbackTrimOut: number): number {
  if (!asset) return fallbackTrimOut
  if (isTimelineImageAsset(asset)) {
    return Math.max(fallbackTrimOut, MAX_IMAGE_ASSET_DURATION_TICKS)
  }
  return asset.duration
}

/**
 * Find the nearest snap target within threshold (for playhead snapping)
 * Returns a full SnapResult with information about whether a snap occurred and to which target
 */
export function snapToNearestWithResult(
  ticks: number,
  targets: SnapTarget[],
  threshold: number = SNAP_THRESHOLD_TICKS,
): SnapResult {
  let bestSnap: SnapResult = {
    snappedTicks: ticks,
    isSnapped: false,
    snapTarget: null,
  }
  let bestDistance = threshold

  for (const target of targets) {
    const distance = Math.abs(ticks - target.ticks)
    if (distance < bestDistance) {
      bestDistance = distance
      bestSnap = {
        snappedTicks: target.ticks,
        isSnapped: true,
        snapTarget: target,
      }
    }
  }

  return bestSnap
}

/**
 * Snap target with position and type for visual feedback
 */
export interface SnapTarget {
  ticks: number
  type: 'clip-start' | 'clip-end' | 'playhead' | 'timeline-start'
  clipId?: string
}

/**
 * Result of snap calculation
 */
export interface SnapResult {
  snappedTicks: number
  isSnapped: boolean
  snapTarget: SnapTarget | null
}

/**
 * Get all snap targets from clips and playhead
 */
export function getSnapTargets(
  clips: Map<string, Clip>,
  playheadTicks: number,
  excludeClipIds: Set<string> = new Set(),
): SnapTarget[] {
  const targets: SnapTarget[] = [
    { ticks: 0, type: 'timeline-start' },
    { ticks: playheadTicks, type: 'playhead' },
  ]

  for (const [clipId, clip] of clips) {
    if (excludeClipIds.has(clipId)) continue

    targets.push({
      ticks: clip.startOnTimeline,
      type: 'clip-start',
      clipId,
    })

    targets.push({
      ticks: clip.startOnTimeline + clip.duration,
      type: 'clip-end',
      clipId,
    })
  }

  return targets
}

/**
 * Calculate snapped position for a clip
 * Checks both clip start and clip end against snap targets
 */
export function snapPosition(
  proposedTicks: number,
  clipDuration: number,
  targets: SnapTarget[],
  threshold: number = SNAP_THRESHOLD_TICKS,
): SnapResult {
  let bestSnap: SnapResult = {
    snappedTicks: proposedTicks,
    isSnapped: false,
    snapTarget: null,
  }
  let bestDistance = threshold

  // Check clip START against all targets
  for (const target of targets) {
    const distance = Math.abs(proposedTicks - target.ticks)
    if (distance < bestDistance) {
      bestDistance = distance
      bestSnap = {
        snappedTicks: target.ticks,
        isSnapped: true,
        snapTarget: target,
      }
    }
  }

  // Check clip END against all targets
  const proposedEnd = proposedTicks + clipDuration
  for (const target of targets) {
    const distance = Math.abs(proposedEnd - target.ticks)
    if (distance < bestDistance) {
      bestDistance = distance
      bestSnap = {
        snappedTicks: target.ticks - clipDuration,
        isSnapped: true,
        snapTarget: target,
      }
    }
  }

  return bestSnap
}

/**
 * Clip edge info for multi-clip snapping
 */
export interface ClipEdge {
  /** Offset from anchor clip's start position */
  offsetFromAnchor: number
  /** Duration of this clip (for calculating end edge) */
  duration: number
}

/**
 * Calculate snapped position for multiple clips being dragged together.
 * Checks all clips' start and end edges against snap targets.
 *
 * @param proposedAnchorTicks - Proposed start position for the anchor clip
 * @param clipEdges - Array of clip edges with their offsets from anchor
 * @param targets - Snap targets to check against
 * @param threshold - Snap threshold in ticks
 * @returns SnapResult with the adjusted anchor position
 */
export function snapMultipleClips(
  proposedAnchorTicks: number,
  clipEdges: ClipEdge[],
  targets: SnapTarget[],
  threshold: number = SNAP_THRESHOLD_TICKS,
): SnapResult {
  let bestSnap: SnapResult = {
    snappedTicks: proposedAnchorTicks,
    isSnapped: false,
    snapTarget: null,
  }
  let bestDistance = threshold

  // Check each clip's edges
  for (const edge of clipEdges) {
    const clipStart = proposedAnchorTicks + edge.offsetFromAnchor
    const clipEnd = clipStart + edge.duration

    // Check clip START against all targets
    for (const target of targets) {
      const distance = Math.abs(clipStart - target.ticks)
      if (distance < bestDistance) {
        bestDistance = distance
        // Adjust anchor position so this clip's start aligns with target
        bestSnap = {
          snappedTicks: target.ticks - edge.offsetFromAnchor,
          isSnapped: true,
          snapTarget: target,
        }
      }
    }

    // Check clip END against all targets
    for (const target of targets) {
      const distance = Math.abs(clipEnd - target.ticks)
      if (distance < bestDistance) {
        bestDistance = distance
        // Adjust anchor position so this clip's end aligns with target
        bestSnap = {
          snappedTicks: target.ticks - edge.duration - edge.offsetFromAnchor,
          isSnapped: true,
          snapTarget: target,
        }
      }
    }
  }

  return bestSnap
}

/**
 * Resolution action for handling overlaps
 */
export interface OverlapResolution {
  type: 'delete' | 'trim-left' | 'trim-right' | 'split'
  clipId: string
  // For trim-left: new start position and trimIn
  trimLeft?: { startOnTimeline: number; trimIn: number; duration: number; volumePoints?: Clip['volumePoints'] }
  // For trim-right: new duration and trimOut
  trimRight?: { duration: number; trimOut: number; volumePoints?: Clip['volumePoints'] }
  // For split: split point and second half clip data
  split?: { splitTicks: number; secondHalf: Omit<Clip, 'id'> }
}

/**
 * Resolve overlaps by trimming/splitting/removing existing clips to make room for the dropped clip.
 * The dropped clip always wins - existing clips are trimmed, split, or deleted as needed.
 *
 * @param droppedStart - Start position of the dropped clip (in ticks)
 * @param droppedEnd - End position of the dropped clip (in ticks)
 * @param trackIndex - Track index where the clip is being dropped
 * @param sequenceId - Sequence ID to filter clips (only clips in this sequence are considered)
 * @param clips - All clips in the project
 * @param excludeClipIds - Clip IDs to exclude (the dropped clip(s) themselves)
 * @param splitLinkGroups - Share across all tracks in one overwrite to retain right-hand A/V pairing
 * @returns Array of resolutions to apply
 */
export function resolveOverlaps(
  droppedStart: number,
  droppedEnd: number,
  trackIndex: number,
  sequenceId: string,
  clips: Map<string, Clip>,
  excludeClipIds: Set<string> = new Set(),
  splitLinkGroups: Map<string, string> = new Map(),
): OverlapResolution[] {
  const resolutions: OverlapResolution[] = []

  // Find all clips on the same track and sequence that overlap with the dropped clip
  for (const [clipId, clip] of clips) {
    if (excludeClipIds.has(clipId)) continue
    if (clip.trackIndex !== trackIndex) continue
    if (clip.sequenceId !== sequenceId) continue

    const clipStart = clip.startOnTimeline
    const clipEnd = clipStart + clip.duration

    // Check for overlap: droppedStart < clipEnd && droppedEnd > clipStart
    if (droppedStart >= clipEnd || droppedEnd <= clipStart) continue

    // Case 1: Complete coverage - delete the existing clip
    if (droppedStart <= clipStart && droppedEnd >= clipEnd) {
      resolutions.push({ type: 'delete', clipId })
      continue
    }

    // Case 2: Overlap at end - trim right edge
    if (droppedStart < clipEnd && droppedStart > clipStart && droppedEnd >= clipEnd) {
      const newDuration = droppedStart - clipStart
      const trimDelta = clip.duration - newDuration
      const newTrimOut = clip.loop ? clip.trimOut : clip.trimOut - trimDelta * clipSpeed(clip)

      resolutions.push({
        type: 'trim-right',
        clipId,
        trimRight: {
          duration: newDuration,
          trimOut: newTrimOut,
          volumePoints: windowVolumePoints(clip.volumePoints, 0, newDuration),
        },
      })
      continue
    }

    // Case 3: Overlap at start - trim left edge
    if (droppedEnd > clipStart && droppedEnd < clipEnd && droppedStart <= clipStart) {
      const trimDelta = droppedEnd - clipStart
      const newStart = droppedEnd
      const newTrimIn = sourceTimeAt(clip, trimDelta)
      const newDuration = clip.duration - trimDelta

      resolutions.push({
        type: 'trim-left',
        clipId,
        trimLeft: {
          startOnTimeline: newStart,
          trimIn: newTrimIn,
          volumePoints: windowVolumePoints(clip.volumePoints, trimDelta, newDuration),
          duration: newDuration,
        },
      })
      continue
    }

    // Case 4: Split - dropped clip is entirely within existing clip
    if (droppedStart > clipStart && droppedEnd < clipEnd) {
      // The right survivor is a new segment, not another member of the left
      // segment's A/V pair. Reuse a fresh group only for partners split by the
      // same overwrite, including when audio and video resolve in separate calls.
      let linkGroupId: string | undefined
      if (clip.linkGroupId) {
        const key = JSON.stringify([sequenceId, clip.linkGroupId, droppedStart, droppedEnd])
        linkGroupId = splitLinkGroups.get(key)
        if (!linkGroupId) {
          linkGroupId = uuid()
          splitLinkGroups.set(key, linkGroupId)
        }
      }
      resolutions.push({
        type: 'split',
        clipId,
        split: {
          splitTicks: droppedStart,
          secondHalf: {
            ...clip,
            linkGroupId,
            volumePoints: windowVolumePoints(clip.volumePoints, droppedEnd - clipStart, clipEnd - droppedEnd),
            assetId: clip.assetId,
            trackIndex: clip.trackIndex,
            startOnTimeline: droppedEnd,
            duration: clipEnd - droppedEnd,
            trimIn: sourceTimeAt(clip, droppedEnd - clipStart),
            trimOut: clip.trimOut,
            sequenceId: clip.sequenceId,
            nestedSequenceId: clip.nestedSequenceId,
            isLinked: clip.isLinked,
          },
        },
      })
      continue
    }

    // Case 5 removed: redundant with Cases 2 and 3
    // Case 5a (droppedStart < clipStart && droppedEnd < clipEnd) is handled by Case 3
    // Case 5b (droppedStart > clipStart && droppedEnd > clipEnd) is handled by Case 2
  }

  return resolutions
}

/**
 * Compute which clips overlap another clip on the same track.
 *
 * `sortedClips` must be ordered by trackIndex, then startOnTimeline. Linear
 * sweep: an unflagged clip always becomes the running-max holder for its
 * track, so every overlap partner of a later clip is either already flagged
 * or is the holder — the flag set matches a full pairwise comparison.
 * Clips in `excludeClipIds` (e.g. mid-drag) are skipped entirely.
 */
export function computeOverlappingClipIds(
  sortedClips: readonly Clip[],
  excludeClipIds?: ReadonlySet<string>,
): Set<string> {
  const overlapping = new Set<string>()

  let currentTrack: number | null = null
  let runningMaxEnd = -Infinity
  let holderId: string | null = null
  for (const clip of sortedClips) {
    if (excludeClipIds?.has(clip.id)) continue
    if (clip.trackIndex !== currentTrack) {
      currentTrack = clip.trackIndex
      runningMaxEnd = -Infinity
      holderId = null
    }
    const start = clip.startOnTimeline
    const end = start + clip.duration
    if (holderId !== null && start < runningMaxEnd) {
      overlapping.add(clip.id)
      overlapping.add(holderId)
    }
    if (end > runningMaxEnd) {
      runningMaxEnd = end
      holderId = clip.id
    }
  }

  return overlapping
}
